<script setup lang="ts">
    import { ChevronDown, X, LoaderCircle } from 'lucide-vue-next';
    import {
        ComboboxAnchor,
        ComboboxContent,
        ComboboxInput,
        ComboboxItem,
        ComboboxLabel,
        ComboboxPortal,
        ComboboxVirtualizer,
        ComboboxEmpty,
        ComboboxRoot,
        ComboboxSeparator,
        ComboboxTrigger,
        ComboboxViewport,
        TagsInputInput,
        TagsInputItem,
        TagsInputItemDelete,
        TagsInputItemText,
        TagsInputRoot,
        useFilter,
    } from 'reka-ui';
    import { computed, ref } from 'vue';
    import type { SelectOption } from '@/types';
    import { useI18n } from 'vue-i18n';
    import { cn } from '@/lib/utils';

    interface Props {
        modelValue: string[] | number[];
        options: SelectOption[] | Record<string, any>[];
        placeholder?: string;
        translate?: boolean;
        loading?: boolean;
        grouped?: boolean;
        showAddon?: boolean;
        deletedIsSelectable?: boolean;
        labelBy?: string;
        valueBy?: string;
        disabled?: boolean;
        position?: 'inline' | 'popper';
    }

    const props = withDefaults(defineProps<Props>(), {
        translate: false,
        placeholder: '',
        loading: false,
        grouped: false,
        showAddon: true,
        deletedIsSelectable: false,
        labelBy: 'label',
        valueBy: 'value',
        position: 'popper',
    });

    const emit = defineEmits<{
        'update:modelValue': [value: string[] | number[]];
    }>();

    const { t } = useI18n();
    const { contains } = useFilter({ sensitivity: 'base' });

    const searchTerm = ref('');

    const values = computed({
        get: () => props.modelValue.map((value) => value.toString()),
        set: (newValues) => {
            emit('update:modelValue', newValues);
        },
    });

    const getOptionValue = (option: SelectOption | Record<string, any>): string => {
        return (option as Record<string, any>)[props.valueBy]?.toString() || '';
    };

    const getOptionLabel = (option: SelectOption | Record<string, any>): string => {
        const label = (option as Record<string, any>)[props.labelBy];
        return props.translate ? t(label) : label;
    };

    const translatedOptions = computed(() =>
        props.options.map((option) => ({
            ...option,
            value: getOptionValue(option),
            label: getOptionLabel(option),
            addon: option.addon,
            deleted_at: option.deleted_at,
            is_group: option.is_group,
            is_seperator: option.is_seperator,
        })),
    );

    const filteredOptions = computed(() =>
        translatedOptions.value.filter(
            (option) =>
                contains(option.label, searchTerm.value) && !values.value.includes(option.value),
        ),
    );

    const placeholderWithDefault = computed(
        () => props.placeholder || t('forms.select_placeholder'),
    );

    const selectedOptions = computed(() => {
        const map = new Map();
        values.value.forEach((value) => {
            const option = translatedOptions.value.find((opt) => opt.value === value);
            if (option) {
                map.set(value, option);
            }
        });
        return map;
    });

    const open = ref(false);
    const comboboxInputRef = ref<InstanceType<typeof ComboboxInput> | null>(null);

    const handleKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Tab') {
            open.value = false;
        }
    };

    const handleInteractOutside = (event: any) => {
        if (event.target !== comboboxInputRef.value?.$el) {
            open.value = false;
        }
    };
</script>

<template>
    <ComboboxRoot
        v-model="values"
        v-model:open="open"
        :multiple="true"
        ignore-filter
        class="group relative"
        :disabled="disabled"
    >
        <ComboboxAnchor
            class="group-data-[disabled]:bg-faded text-foreground data-[placeholder]:text-faded-foreground border-input flex min-h-7.5 w-full items-center justify-between gap-[5px] rounded-md border bg-white p-0.75 text-xs leading-none shadow-xs outline-none"
        >
            <TagsInputRoot
                v-model="values"
                delimiter=""
                class="flex flex-1 flex-wrap items-center gap-1 rounded-lg"
            >
                <TagsInputItem
                    v-if="!loading"
                    v-for="value in values"
                    :key="value"
                    :value="value"
                    class="bg-primary/10 aria-[current=true]:bg-primary text-foreground border-primary/20 flex items-center justify-center gap-2 rounded border px-1.5 py-0.5"
                    :class="{
                        'bg-destructive/10! border-destructive/20!':
                            selectedOptions.get(value)?.deleted_at,
                        'bg-accent/10! border-accent/20!':
                            selectedOptions.get(value)?.addon === 'userGroup',
                    }"
                >
                    <TagsInputItemText class="text-xs">
                        {{ selectedOptions.get(value)?.label }}
                        <span v-if="selectedOptions.get(value)?.deleted_at">
                            ({{ t('forms.is_deleted') }})
                        </span>
                    </TagsInputItemText>
                    <TagsInputItemDelete>
                        <X class="size-3 cursor-pointer" />
                    </TagsInputItemDelete>
                </TagsInputItem>

                <ComboboxInput
                    v-model="searchTerm"
                    as-child
                    @focus="
                        () => {
                            open = true;
                        }
                    "
                >
                    <TagsInputInput
                        ref="comboboxInputRef"
                        :placeholder="placeholderWithDefault"
                        class="placeholder:text-faded-foreground h-5.5 flex-1 rounded !bg-transparent pl-1.75 focus:outline-none"
                        @keydown.enter.prevent
                        @keydown="handleKeydown"
                    />
                </ComboboxInput>
            </TagsInputRoot>

            <ComboboxTrigger class="shrink-0 pr-1.75">
                <ChevronDown
                    v-if="!loading"
                    class="text-foreground/50 size-4 cursor-pointer"
                />
                <LoaderCircle
                    v-else
                    class="text-foreground/50 ml-auto size-4 animate-spin"
                />
            </ComboboxTrigger>
        </ComboboxAnchor>

        <ComboboxPortal>
            <ComboboxContent
                :class="
                    cn(
                        'data-[state=open]:animate-in data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 mt-1 w-full overflow-hidden rounded-md border bg-white shadow-md will-change-[opacity,transform]',
                        position === 'inline'
                            ? 'absolute max-h-60 w-full origin-top'
                            : 'max-h-(--reka-combobox-content-available-height) w-[var(--reka-popper-anchor-width)] origin-[var(--reka-popper-transform-origin)]',
                    )
                "
                align="start"
                side-flip
                :side-offset="4"
                @pointer-down-outside.prevent="handleInteractOutside"
                :position="position"
            >
                <ComboboxViewport
                    :class="
                        cn(
                            'overflow-y-auto p-1',
                            position === 'inline' ? 'max-h-60' : 'max-h-(--reka-combobox-content-available-height)',
                        )
                    "
                >
                <ComboboxVirtualizer
                    v-if="!grouped"
                    v-slot="{ option }"
                    :options="filteredOptions"
                    :text-content="(opt: SelectOption) => opt.label"
                    :estimate-size="24"
                >
                    <ComboboxLabel
                        v-if="option.is_group"
                        :key="option.label"
                        class="text-foreground/50 mx-2 w-full px-0 py-1.5 text-xs leading-none"
                    >
                        {{ option.label }}
                    </ComboboxLabel>
                    <div
                        v-else-if="option.is_seperator"
                        class="flex h-6 w-full items-center px-2"
                    >
                        <ComboboxSeparator
                            class="bg-border h-[1px] w-full"
                            :key="`${option.value}`"
                        />
                    </div>
                    <ComboboxItem
                        v-else
                        class="text-foreground data-[disabled]:text-muted-foreground data-[highlighted]:bg-primary/10 relative flex w-full cursor-pointer items-center px-2 py-1.5 text-xs leading-none select-none data-[disabled]:pointer-events-none data-[highlighted]:outline-none"
                        :value="option.value"
                    >
                        <span class="truncate">
                            {{ option.label }}
                        </span>
                        <span
                            v-if="showAddon && option.addon"
                            class="text-muted-foreground ml-auto text-[10px]"
                        >
                            {{ option.addon }}
                        </span>
                    </ComboboxItem>
                </ComboboxVirtualizer>

                <ComboboxEmpty>
                    <span class="text-muted-foreground p-2 text-xs">{{
                        t('forms.no_options')
                    }}</span>
                </ComboboxEmpty>
                </ComboboxViewport>
            </ComboboxContent>
        </ComboboxPortal>
    </ComboboxRoot>
</template>
